import { useContext } from 'react'

import {
  Button,
  Form,
  ModalContext,
  SidebarModal,
  SidebarNoticeBox,
} from '@domain-group/suit-theme'

import type { SidebarModalProps } from '@domain-group/suit-theme'

import useAgency, { useAgencyForm } from '@hooks/useAgency'
import useBillingTypes from '@hooks/useBillingTypes'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type { AgencyModel } from '@domain-group/realhub-backend-api-types'

const customRequiredFields = [
  {
    key: 'subscription_start_date',
    validation: (value: string, { billing_type_id }: AgencyModel) => (billing_type_id ? !!value : true),
  },
  {
    key: 'subscription_fee',
    validation: (value: string, { billing_type_id }: AgencyModel) => (billing_type_id ? !!value : true),
  },
]

interface ModalCallbacks {
  callbacks: {
    updateAgency: (payload: HandleCallbackActionParams) => Promise<object>,
  },
}

const ManageAgencySubscriptionModal = (props: SidebarModalProps & ModalCallbacks) => {
  const { callbacks, modalKey = 'ManageAgencySubscriptionModal', showModal } = props
  const { closeModal, updateAgency } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData[modalKey] || {}
  const { agency } = modalPayload

  const {
    callbacks: { updateAgency: updateFn },
    updating,
  } = useAgency(agency)

  const {
    entityState,
    entityState: {
      billing_type_id,
      subscription_fee,
      subscription_start_date,
      subscription_user_limit,
    },
    setEntityState,
    errors,
    handlers,
    saveEnabled,
  } = useAgencyForm(agency, { validateOn: 'change', customRequiredFields })

  const billingTypes = useBillingTypes({
    performHttpRequests: !!agency?.id,
    filters: {
      charges_type: 'subscription',
    },
  })
  const { filteredBillingTypes, hasFilteredBillingTypes } = billingTypes

  const updateAgencyPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: 'Subscription Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Subscription" />

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Title>Important Notice</SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            A Zuora Account Number must be present against the agency for a subscription to be added.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        <Form>
          {/*
           // @ts-expect-error TS2322 */}
          <Form.Field label="* Subscription Type" marginTop={['large', 0]}>
            <select
              disabled={!agency?.zuora_account_number}
              // @ts-expect-error TS2322
              errorMessage={errors.billing_type_id}
              name="billing_type_id"
              value={billing_type_id}
              onChange={e => setEntityState({
                billing_type_id: e.target.value,
                subscription_fee: e.target.value === '' ? '' : subscription_fee,
              })}
            >
              <option value="">No Subscription</option>
              {filteredBillingTypes.map(billingType => (
                <option key={billingType.id} value={billingType.id}>{billingType.title}</option>
              ))}
            </select>
          </Form.Field>

          <Form.Field
            direction="column"
            errorMessage={errors.subscription_fee}
            label="* Subscription Fee"
            marginTop="large"
          >
            <input
              disabled={!agency?.zuora_account_number}
              name="subscription_fee"
              type="number"
              value={subscription_fee}
              {...handlers}
            />
          </Form.Field>

          <Form.Field direction="column" label="Subscription User Limit" marginTop="large">
            <input
              data-value-type="integer"
              disabled={!agency?.zuora_account_number}
              name="subscription_user_limit"
              placeholder="Leave blank for unlimited"
              value={subscription_user_limit}
              {...handlers}
            />
          </Form.Field>

          <Form.Field
            direction="column"
            errorMessage={errors.subscription_start_date}
            label="Start Date"
            marginTop="large"
          >
            <input
              disabled={!agency?.zuora_account_number}
              name="subscription_start_date"
              type="date"
              value={subscription_start_date}
              {...handlers}
            />
          </Form.Field>

          {agency?.billing_type_id && (
            <>
              <Form.Field label="Zuora Charge Number" marginTop="large">
                <input disabled value={agency?.zuora_charge_number} />
              </Form.Field>

              <Form.Field label="Zuora Subscription Number" marginTop="large">
                <input disabled value={agency?.zuora_subscription_number} />
              </Form.Field>
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => updateAgency(updateAgencyPayload)}
          size="large"
        >
          {agency.billing_type_id ? 'Update Subscription' : 'Add Subscription'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: SidebarModalProps & ModalCallbacks) => (
  <SidebarModal.RenderController {...props}>
    <ManageAgencySubscriptionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
